import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatSafeDate } from "@/lib/dateUtils";
import { cn } from "@/lib/utils";
import { Transaction } from "@/services/salesService";
import { 
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useState, useEffect } from "react";
import { debugLog } from "@/utils/debugConfig";

interface TransactionsCardProps {
  data: Transaction[];
  isLoading: boolean;
  onExportCSV: () => void;
}

export function TransactionsCard({
  data,
  isLoading,
  onExportCSV
}: TransactionsCardProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil((data?.length || 0) / itemsPerPage));
  
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTransactions = data?.slice(startIndex, endIndex) || [];

  useEffect(() => {
    debugLog("TRANSACTIONS", "TransactionsCard component render", { 
      dataLength: data?.length || 0, 
      isLoading,
      firstTransaction: data && data.length > 0 ? {
        id: data[0].id,
        date: data[0].date,
        course: data[0].course,
        student: data[0].student,
        amount: data[0].amount,
        status: data[0].status,
        hasInvoiceUrl: !!data[0].stripe_invoice_url
      } : 'No transactions'
    });
    
    // Reset to first page when data changes
    if (currentPage > 1 && data?.length <= itemsPerPage) {
      setCurrentPage(1);
    }
  }, [data, isLoading, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink
            isActive={currentPage === i}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }
    return items;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium">Transactions</h3>
          </div>
          <div className="h-64 flex items-center justify-center">
            <div className="text-muted-foreground">Loading transactions...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium">Transactions</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={onExportCSV}
              className="gap-2"
              disabled={true}
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
          <div className="h-64 flex items-center justify-center">
            <div className="text-muted-foreground">No transactions to display</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium">Transactions</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={onExportCSV}
            className="gap-2"
            disabled={data.length === 0}
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>

        {data.length === 0 ? (
          <div className="h-64 flex items-center justify-center">
            <div className="text-muted-foreground">No transactions found</div>
          </div>
        ) : (
          <>
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Course</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Invoice</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        {formatSafeDate(transaction.date, 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {transaction.course}
                      </TableCell>
                      <TableCell>
                        {transaction.student}
                      </TableCell>
                      <TableCell>
                        ${transaction.amount.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium",
                            transaction.status === "completed"
                              ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                              : "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400"
                          )}
                        >
                          {transaction.status === "completed"
                            ? "Completed"
                            : "Pending"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {transaction.stripe_invoice_url ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            asChild
                          >
                            <a
                              href={transaction.stripe_invoice_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="h-4 w-4" />
                              <span className="sr-only">View Invoice</span>
                            </a>
                          </Button>
                        ) : (
                          <span className="text-muted-foreground text-sm">N/A</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex justify-center">
                <Pagination>
                  <PaginationContent>
                    {currentPage > 1 && (
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() => handlePageChange(currentPage - 1)}
                        />
                      </PaginationItem>
                    )}

                    {renderPaginationItems()}

                    {currentPage < totalPages && (
                      <PaginationItem>
                        <PaginationNext
                          onClick={() => handlePageChange(currentPage + 1)}
                        />
                      </PaginationItem>
                    )}
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
